/*
 *
 * Auth reducer
 *
 */
import { createSlice } from '@reduxjs/toolkit';

import {
  setAuthAction,
  getCurrentUserAction,
  performLogoutSuccessAction,
  getCurrentUserFailedAction,
  getCurrentUserSuccessAction,
  setPageTitle,
  sessionExpiredAction,
  resetAuthStateAction,
  updateViewRoleAct,
  getActiveRolesAct,
} from './actions';
import { LOADING_STATES } from '../../constants/loading-state.constant';

export const initialState = {
  token: '',
  expiresIn: '',
  loggedInState: 'verifying',
  user: '',
  error: '',
  pageTitle: 'Welcome to Actively',
  viewRole: {
    roleId: 0,
    roleName: '',
  },
  rules: [],
  hasVolunteerModule:
    process.env.REACT_APP_HAS_VOLUNTEER === undefined ||
    process.env.REACT_APP_HAS_VOLUNTEER === '1',
  hasSupporterModule:
    process.env.REACT_APP_HAS_SUPPORTER === undefined ||
    process.env.REACT_APP_HAS_SUPPORTER === '1',
  hasMealModule:
    process.env.REACT_APP_MEAL === undefined ||
    process.env.REACT_APP_MEAL === '1',
  hasHostingModule:
    process.env.REACT_APP_HOSTING === undefined ||
    process.env.REACT_APP_HOSTING === '1',
  hasHubSpotModule:
    process.env.REACT_APP_HUB_SPOT === undefined ||
    process.env.REACT_APP_HUB_SPOT === '1',
  hasTeamGroupModule:
    process.env.REACT_APP_TEAM_GROUP === undefined ||
    process.env.REACT_APP_TEAM_GROUP === '1',
  allowDepartureTime:
    process.env.REACT_APP_ALLOW_DEPARTURE_TIME_UPDATE === undefined ||
    process.env.REACT_APP_ALLOW_DEPARTURE_TIME_UPDATE === '1',
  hasCapModule:
    process.env.REACT_APP_CAP_MODULE === undefined ||
    process.env.REACT_APP_CAP_MODULE === '1',
  hasBikeModule:
    process.env.REACT_APP_BIKE_MODULE === undefined ||
    process.env.REACT_APP_BIKE_MODULE === '1',
  hasFTTRILicenseModule:
    process.env.REACT_APP_HAS_FTTRI_LICENSE_MODULE === undefined ||
    process.env.REACT_APP_HAS_FTTRI_LICENSE_MODULE === '1',
  client: process.env.CLIENT ? process.env.CLIENT : 'actively',
  hasNotification: process.env.REACT_APP_NOTIFICATION === '1',
  hasGlobalDashboard: process.env.REACT_APP_GLOBAL_DASHBOARD === '1',
  hasUserRepresentative: process.env.REACT_APP_USER_REPRESENTATIVE === '1',
  hasMemberSource: process.env.REACT_APP_MEMBER_SOURCE === '1',
  showDocumentStatusStats: process.env.REACT_APP_DOCUMENT_STATUS_STATS === '1',
  activeRoles: [],
  rolesLoadingState: LOADING_STATES.IDLE,
};

/* eslint-disable default-case, no-param-reassign */
const authSlice = createSlice({
  name: 'Auth',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(setAuthAction, (state, { payload }) => {
        // Redux Toolkit allows us to write "mutating" logic in reducers. It
        // doesn't actually mutate the state because it uses the immer library,
        // which detects changes to a "draft state" and produces a brand new
        // immutable state based off those changes
        state.token = payload.token;
        state.expiresIn = payload.expires_in;
      })
      .addCase(performLogoutSuccessAction, state => {
        state.token = '';
        state.expiresIn = null;
        state.user = '';
        state.error = '';
        state.pageTitle = '';
        state.loggedInState = 'logout';
        state.viewRole = {
          id: 0,
          roleName: '',
        };
      })
      .addCase(sessionExpiredAction, state => {
        state.loggedInState = 'expired';
      })
      .addCase(getCurrentUserAction, state => {
        state.loggedInState = 'verifying';
        state.error = '';
        state.user = '';
      })
      .addCase(getCurrentUserSuccessAction, (state, { payload }) => {
        state.loggedInState = 'alive';
        state.user = payload;
        state.viewRole = payload.role;
        state.rules = payload.rules || [];
        state.error = '';
      })
      .addCase(getCurrentUserFailedAction, (state, { payload }) => {
        state.loggedInState = 'failed';
        state.user = '';
        state.error = payload;
      })
      .addCase(setPageTitle, (state, { payload }) => {
        state.pageTitle = payload;
      })
      .addCase(updateViewRoleAct, (state, { payload }) => {
        const { viewRoleChangeState, viewRole, rules } = payload;
        switch (viewRoleChangeState) {
          case 1:
            state.loggedInState = 'verifying';
            break;
          case 2:
            if (viewRole.id === state.user?.role?.id) {
              state.rules = state.user.rules || [];
            } else {
              state.rules = rules;
            }
            state.loggedInState = 'alive';
            break;
          case 3:
            state.loggedInState = 'alive';
            break;
        }
        state.viewRole = viewRole;
      })
      .addCase(getActiveRolesAct, (state, { payload }) => {
        const { rolesLoadingState, roles } = payload;
        switch (rolesLoadingState) {
          case LOADING_STATES.SUCCESS:
            state.rolesLoadingState = rolesLoadingState;
            state.activeRoles = roles;
            break;
          default:
            state.rolesLoadingState = rolesLoadingState;
        }
      })
      .addCase(resetAuthStateAction, () => initialState);
  },
});

export default authSlice.reducer;
